/**
 * RapoarteTab - Componenta pentru tab-ul Rapoarte
 * Afișează rapoarte de activitate flotă cu filtre și export
 */
import { memo, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Calendar, Download, X } from 'lucide-react';
import { format } from 'date-fns';

/**
 * Componenta RapoarteTab
 */
const RapoarteTab = memo(function RapoarteTab({
  trips,
  drivers,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedDriverFilter,
  setSelectedDriverFilter,
  fetchTrips,
  handleExport,
  setOpenCentralizatorAngajatiDialog,
  loading
}) {
  // State pentru filtru "Închis de"
  const [closedByFilter, setClosedByFilter] = useState('all');

  // Calculează distanța pentru o cursă
  const calculateDistance = (trip) => {
    if (trip.end_km && trip.start_km) {
      return (trip.end_km - trip.start_km).toFixed(1);
    }
    return 'N/A';
  };

  // Filtrare șoferi - doar cei care au curse în perioada selectată
  const driversWithTrips = useMemo(() => {
    // Obține ID-urile șoferilor care au curse
    const driverIdsWithTrips = new Set(trips.map(t => t.driver_id).filter(Boolean));
    
    // Filtrează lista de drivers să includă doar cei cu curse
    return drivers.filter(d => driverIdsWithTrips.has(d.id));
  }, [trips, drivers]);

  // Filtrare curse după "Închis de"
  const filteredTrips = useMemo(() => {
    if (closedByFilter === 'all') {
      return trips;
    }
    
    if (closedByFilter === 'sofer') {
      // Curse închise de șoferul care a făcut cursa (el însuși)
      return trips.filter(trip => 
        trip.closed_by && trip.driver_name && 
        trip.closed_by.toLowerCase() === trip.driver_name.toLowerCase()
      );
    }
    
    if (closedByFilter === 'admin') {
      // Curse închise de altcineva (admin/fleet manager), nu de șofer
      return trips.filter(trip => 
        trip.closed_by && trip.driver_name && 
        trip.closed_by.toLowerCase() !== trip.driver_name.toLowerCase()
      );
    }
    
    return trips;
  }, [trips, closedByFilter]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Rapoarte Curse</CardTitle>
          <CardDescription>Vizualizează și exportă rapoarte de activitate flotă</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Filtre */}
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Data început</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left" data-testid="start-date-picker">
                    <Calendar className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Selectează data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent mode="single" selected={startDate} onSelect={setStartDate} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex-1">
              <Label>Data sfârșit</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left" data-testid="end-date-picker">
                    <Calendar className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'Selectează data'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent mode="single" selected={endDate} onSelect={setEndDate} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex-1">
              <Label>Șofer</Label>
              <Select 
                value={selectedDriverFilter} 
                onValueChange={setSelectedDriverFilter}
              >
                <SelectTrigger data-testid="driver-filter-select">
                  <SelectValue placeholder="Toți șoferii" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toți șoferii ({driversWithTrips.length})</SelectItem>
                  {driversWithTrips.map((driver) => {
                    const fullName = (driver.nume && driver.prenume) 
                      ? `${driver.nume} ${driver.prenume}` 
                      : driver.username;
                    return (
                      <SelectItem key={driver.id} value={driver.id}>
                        {fullName}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Închis de</Label>
              <Select 
                value={closedByFilter} 
                onValueChange={setClosedByFilter}
              >
                <SelectTrigger data-testid="closed-by-filter-select">
                  <SelectValue placeholder="Toți" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate cursele</SelectItem>
                  <SelectItem value="admin">Admin / Fleet Manager</SelectItem>
                  <SelectItem value="sofer">Șofer (singur)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchTrips} data-testid="filter-trips-button">
              Filtrează
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setStartDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
                setEndDate(new Date());
                setSelectedDriverFilter('all');
                setClosedByFilter('all');
              }}
              data-testid="reset-filters-button"
              className="text-slate-600"
            >
              <X className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>

          {/* Export curse filtrate */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-blue-900 flex items-center gap-2">
                  📋 Export Curse Filtrate
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Exportă cursele din tabelul de mai jos (conform filtrelor selectate)
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleExport('pdf')}
                  disabled={loading || filteredTrips.length === 0}
                  data-testid="export-filtered-pdf-button"
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                  size="sm"
                >
                  <Download className="w-4 h-4" />
                  PDF
                </Button>
                <Button
                  onClick={() => handleExport('excel')}
                  disabled={loading || filteredTrips.length === 0}
                  data-testid="export-filtered-excel-button"
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                >
                  <Download className="w-4 h-4" />
                  Excel
                </Button>
                <Button
                  onClick={() => handleExport('csv')}
                  disabled={loading || filteredTrips.length === 0}
                  data-testid="export-filtered-csv-button"
                  className="flex items-center gap-2"
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-4 h-4" />
                  CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Centralizator Angajați */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-green-900 flex items-center gap-2">
                  👥 Centralizator Angajați
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  Export rezumat: toți angajații, kilometri parcurși, număr curse per lună
                </p>
              </div>
              <button
                onClick={() => setOpenCentralizatorAngajatiDialog(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md text-sm"
              >
                👥 Export Centralizator
              </button>
            </div>
          </div>

          {/* Tabel curse */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Șofer</TableHead>
                <TableHead>Nr. Înmatriculare</TableHead>
                <TableHead>Traseu</TableHead>
                <TableHead className="min-w-[200px]">Scop Deplasare</TableHead>
                <TableHead>KM Start</TableHead>
                <TableHead>KM Final</TableHead>
                <TableHead>Distanță</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Închis de</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTrips.map((trip) => (
                <TableRow key={trip.id} data-testid={`trip-row-${trip.id}`}>
                  <TableCell>
                    {trip.start_time ? new Date(trip.start_time).toLocaleDateString('ro-RO', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit' 
                    }) : '-'}
                  </TableCell>
                  <TableCell className="font-medium">{trip.driver_full_name || trip.driver_name}</TableCell>
                  <TableCell className="font-mono text-sm">{trip.car_license || trip.car_name || '-'}</TableCell>
                  <TableCell className="text-sm">{trip.route || '-'}</TableCell>
                  <TableCell className="text-sm">
                    {trip.scop_deplasare || '-'}
                  </TableCell>
                  <TableCell>{trip.start_km}</TableCell>
                  <TableCell>{trip.end_km || '-'}</TableCell>
                  <TableCell>{calculateDistance(trip)} km</TableCell>
                  <TableCell>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        trip.status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {trip.status === 'completed' ? 'Finalizat' : 'În progres'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {trip.closed_by ? (
                      trip.closed_by.toLowerCase() === (trip.driver_name || '').toLowerCase() ? (
                        <span className="text-sm text-green-600 font-medium">Șofer</span>
                      ) : (
                        <span className="text-sm text-blue-600 font-medium">Admin / Fleet Manager</span>
                      )
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredTrips.length > 0 && (
                <TableRow className="bg-blue-50 font-semibold border-t-2 border-blue-200">
                  <TableCell colSpan={7} className="text-right">TOTAL:</TableCell>
                  <TableCell className="text-blue-700">
                    {filteredTrips.reduce((sum, trip) => {
                      const dist = calculateDistance(trip);
                      return sum + (dist !== 'N/A' ? parseFloat(dist) : 0);
                    }, 0).toLocaleString('ro-RO', {maximumFractionDigits: 2})} km
                  </TableCell>
                  <TableCell>{filteredTrips.length} curse</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
              {filteredTrips.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                    Nu există curse pentru perioada selectată
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default RapoarteTab;
